import { useState, useRef, useLayoutEffect } from 'preact/hooks'

/**
 * Holdets beskrivelse i et widget-kort (Playground LX 21-10-2026: «asap», beskrivelsen blev klippet
 * af efter 3 linjer, og kunderne kunne ikke se, hvad holdet er). Teksten vises med linjeskift og
 * klippes kun visuelt. Er den længere end `lines`, kommer en «Læs mere»/«Vis mindre»-knap, der folder
 * hele teksten ud på stedet. Kort tekst ser ud som før, uden knap.
 *
 * Om teksten er for lang, måles i browseren (scrollHeight mod clientHeight) og igen, når kortets
 * bredde ændrer sig (fx når telefonen vendes).
 */
export function ExpandableDesc({ text, cls, lines = 3, more, less }: {
  text: string
  cls: string
  lines?: number
  more: string
  less: string
}) {
  const [open, setOpen] = useState(false)
  const [forLang, setForLang] = useState(false)
  const ref = useRef<HTMLParagraphElement>(null)
  const openRef = useRef(open)
  openRef.current = open

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    // Mål kun i lukket tilstand: åben er der intet klippet væk.
    const maal = () => { if (!openRef.current) setForLang(el.scrollHeight > el.clientHeight + 1) }
    maal()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(maal)
    ro.observe(el)
    return () => ro.disconnect()
  }, [text, lines])

  return (
    <div class="hsw-desc-wrap">
      <p
        ref={ref}
        class={`${cls} hsw-desc${open ? ' hsw-desc-open' : ''}`}
        style={open ? undefined : { WebkitLineClamp: String(lines) }}
      >
        {text}
      </p>
      {(forLang || open) && (
        <button
          type="button"
          class="hsw-desc-toggle"
          aria-expanded={open}
          onClick={(e) => { e.stopPropagation(); setOpen(!open) }}
        >
          {open ? less : more}
        </button>
      )}
    </div>
  )
}

/**
 * CSS til ExpandableDesc. Knappen bruger tekstfarven, ikke brandfarven: en lys brandfarve gjorde
 * knapper usynlige hos Playground LX (21-10-2026).
 */
export const expandableDescStyles = `
  .hsw-desc { white-space: pre-line; overflow-wrap: anywhere; }
  .hsw-desc:not(.hsw-desc-open) { display: -webkit-box; -webkit-box-orient: vertical; overflow: hidden; }
  .hsw-desc.hsw-desc-open { display: block; -webkit-line-clamp: unset; overflow: visible; }
  .hsw-desc-wrap { margin: 0 0 1rem; }
  .hsw-desc-wrap > .hsw-desc { margin-bottom: 0.25rem; }
  .hsw-desc-toggle {
    border: none; background: none; padding: 0.25rem 0; cursor: pointer; font: inherit;
    font-size: 0.85rem; font-weight: 600; color: var(--yoga-text, #171717); text-decoration: underline;
  }
`
